/**
 * useIdleLogout — calls `onIdle` after `idleMs` of no user activity.
 *
 * Tracks mousemove / keydown / click / scroll / touchstart and resets
 * the inactivity timer on each. After the threshold elapses without
 * activity, fires the callback once. The callback (set by main.tsx)
 * triggers signOut() and reloads the page to return the user to the
 * Authenticator's login form.
 *
 * V1.3.0 design note (Q2): the platform's @tensaw/runtime ships a
 * `startIdleTimeout({ idleMs, onIdleSignOut })` effect, but it's tightly
 * coupled to a `useAuthStore.expireSession()` Zustand store that this
 * dashboard doesn't use. Inlining the hook here keeps v1.3.0 scoped to
 * the auth work without requiring full Tensaw platform-store adoption.
 * If the app later migrates to the platform store, swap this for the
 * library's helper.
 */
import { useEffect } from "react";

export interface UseIdleLogoutOptions {
  /** Inactivity threshold in milliseconds. */
  idleMs: number;
  /**
   * Whether the hook is active. Used to gate idle-logout in dev
   * (bypass mode) — no point logging the developer out of nothing.
   */
  enabled: boolean;
  /** Called once when idleMs elapses with no user activity. */
  onIdle: () => void;
}

export function useIdleLogout({ idleMs, enabled, onIdle }: UseIdleLogoutOptions) {
  useEffect(() => {
    if (!enabled || idleMs <= 0) return;

    let lastActivity = Date.now();
    let fired = false;

    const bump = () => {
      lastActivity = Date.now();
    };
    const events: (keyof WindowEventMap)[] = [
      "mousemove",
      "keydown",
      "click",
      "scroll",
      "touchstart",
    ];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const tick = window.setInterval(() => {
      if (fired) return;
      const elapsed = Date.now() - lastActivity;
      if (elapsed >= idleMs) {
        fired = true;
        onIdle();
      }
    }, 1000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      window.clearInterval(tick);
    };
  }, [idleMs, enabled, onIdle]);
}
